/*
╔══════════════════════════════════════════════════════════════════════════════╗
║                        VUE ROUTER - THE NAVIGATION ENGINE                    ║
╚══════════════════════════════════════════════════════════════════════════════╝

🗺️  WHAT IS THIS FILE?

This is the ROUTER CONFIGURATION file. It defines all the routes (pages) in your
application and controls how users navigate between them.

┌─────────────────────────────────────────────────────────────────────────────┐
│ WHY IS THE ROUTER IN A SEPARATE FILE?                                       │
├─────────────────────────────────────────────────────────────────────────────┤
│ • Separation of Concerns: Keeps routing logic separate from app setup      │
│ • Maintainability: Easy to find and modify all routes in one place         │
│ • Scalability: As your app grows, routes don't clutter main.js             │
│ • Reusability: Router configuration can be imported and tested separately  │
└─────────────────────────────────────────────────────────────────────────────┘

🔄 HOW IT WORKS:

  User clicks link → Router matches path → Loads component → Renders in <router-view>

  Example: User visits /about → Router finds route → Loads About1.vue → Shows it


📋 ROUTE CONFIGURATION PARAMETERS
══════════════════════════════════════════════════════════════════════════════

┌─────────────────────────────────────────────────────────────────────────────┐
│ path (String) - REQUIRED                                                    │
├─────────────────────────────────────────────────────────────────────────────┤
│ The URL path for this route                                                │
│                                                                             │
│ Examples:                                                                   │
│   path: '/'              // Home page                                       │
│   path: '/about'         // Simple path                                     │
│   path: '/user/:id'      // Dynamic segment (:id is a parameter)           │
│   path: '/posts/:id?'    // Optional parameter (? makes it optional)       │
└─────────────────────────────────────────────────────────────────────────────┘

┌─────────────────────────────────────────────────────────────────────────────┐
│ name (String) - OPTIONAL but RECOMMENDED                                    │
├─────────────────────────────────────────────────────────────────────────────┤
│ A unique identifier for the route, used for programmatic navigation        │
│                                                                             │
│ Example:                                                                    │
│   name: 'UserProfile'                                                       │
│                                                                             │
│ Usage in code:                                                              │
│   router.push({ name: 'UserProfile', params: { id: 123 } })               │
│   <router-link :to="{ name: 'UserProfile' }">Profile</router-link>         │
└─────────────────────────────────────────────────────────────────────────────┘

┌─────────────────────────────────────────────────────────────────────────────┐
│ component (Component) - REQUIRED                                            │
├─────────────────────────────────────────────────────────────────────────────┤
│ The Vue component to render when this route is matched                     │
│                                                                             │
│ Examples:                                                                   │
│   component: HomePage                    // Imported component              │
│   component: () => import('./About.vue') // Lazy-loaded (better for perf)  │
└─────────────────────────────────────────────────────────────────────────────┘

┌─────────────────────────────────────────────────────────────────────────────┐
│ meta (Object) - OPTIONAL                                                    │
├─────────────────────────────────────────────────────────────────────────────┤
│ Custom data attached to the route, accessible in navigation guards         │
│                                                                             │
│ Common meta properties:                                                     │
│                                                                             │
│   meta: {                                                                   │
│     title: 'Page Title'           // Page title for browser tab           │
│     requiresAuth: true            // Requires user to be logged in        │
│     requiresAdmin: true           // Requires admin privileges            │
│     allowedRoles: ['admin', 'user'] // Array of roles that can access    │
│     layout: 'admin'               // Custom layout to use                │
│     breadcrumb: 'Users'           // Breadcrumb label                     │
│     icon: 'user-icon'             // Icon for navigation                 │
│   }                                                                         │
│                                                                             │
│ You can add ANY custom properties you need!                                │
└─────────────────────────────────────────────────────────────────────────────┘

┌─────────────────────────────────────────────────────────────────────────────┐
│ redirect (String | Object) - OPTIONAL                                       │
├─────────────────────────────────────────────────────────────────────────────┤
│ Redirects to another route                                                 │
│                                                                             │
│ Examples:                                                                   │
│   redirect: '/home'                        // Redirect to path             │
│   redirect: { name: 'HomePage' }          // Redirect to named route      │
│   redirect: to => {                       // Dynamic redirect             │
│     return { path: '/search', query: { q: to.params.query } }             │
│   }                                                                         │
└─────────────────────────────────────────────────────────────────────────────┘

┌─────────────────────────────────────────────────────────────────────────────┐
│ alias (String | Array) - OPTIONAL                                           │
├─────────────────────────────────────────────────────────────────────────────┤
│ Alternative paths that render the same component                           │
│                                                                             │
│ Examples:                                                                   │
│   path: '/home'                                                             │
│   alias: '/'                    // Both /home and / show same component    │
│                                                                             │
│   alias: ['/inicio', '/accueil']  // Multiple aliases                      │
└─────────────────────────────────────────────────────────────────────────────┘

┌─────────────────────────────────────────────────────────────────────────────┐
│ children (Array) - OPTIONAL                                                 │
├─────────────────────────────────────────────────────────────────────────────┤
│ Nested routes - creates parent-child route hierarchy                       │
│                                                                             │
│ Example:                                                                    │
│   {                                                                         │
│     path: '/user',                                                          │
│     component: UserLayout,                                                  │
│     children: [                                                             │
│       { path: '', component: UserHome },           // /user                │
│       { path: 'profile', component: UserProfile }, // /user/profile        │
│       { path: 'settings', component: Settings }    // /user/settings       │
│     ]                                                                       │
│   }                                                                         │
│                                                                             │
│ Parent component must have <router-view> for children to render!           │
└─────────────────────────────────────────────────────────────────────────────┘

┌─────────────────────────────────────────────────────────────────────────────┐
│ props (Boolean | Object | Function) - OPTIONAL                              │
├─────────────────────────────────────────────────────────────────────────────┤
│ Pass route params as props to the component                                │
│                                                                             │
│ Examples:                                                                   │
│   props: true                    // Pass all params as props               │
│   props: { userId: 123 }        // Static props                            │
│   props: route => ({            // Dynamic props from route                │
│     id: Number(route.params.id)                                            │
│   })                                                                        │
└─────────────────────────────────────────────────────────────────────────────┘

┌─────────────────────────────────────────────────────────────────────────────┐
│ beforeEnter (Function) - OPTIONAL                                           │
├─────────────────────────────────────────────────────────────────────────────┤
│ Navigation guard that runs before entering THIS specific route             │
│                                                                             │
│ Example:                                                                    │
│   beforeEnter: (to, from, next) => {                                       │
│     if (!isAuthenticated()) {                                              │
│       next('/login')  // Redirect to login                                 │
│     } else {                                                                │
│       next()          // Allow navigation                                  │
│     }                                                                       │
│   }                                                                         │
└─────────────────────────────────────────────────────────────────────────────┘


📝 COMPLETE EXAMPLE WITH ALL PARAMETERS
══════════════════════════════════════════════════════════════════════════════

{
  path: '/user/:id',                    // URL with dynamic parameter
  name: 'UserProfile',                  // Named route
  component: UserProfilePage,           // Component to render
  
  meta: {                               // Custom metadata
    title: 'User Profile',
    requiresAuth: true,
    allowedRoles: ['admin', 'user'],
    breadcrumb: 'Profile'
  },
  
  props: true,                          // Pass :id as prop
  
  beforeEnter: (to, from, next) => {    // Route-specific guard
    // Check permissions before entering
    if (hasAccess(to.params.id)) {
      next()
    } else {
      next('/forbidden')
    }
  },
  
  children: [                           // Nested routes
    {
      path: 'posts',                    // /user/:id/posts
      component: UserPosts
    }
  ]
}


💡 BEST PRACTICES
══════════════════════════════════════════════════════════════════════════════

✅ DO:
  • Use lazy loading for better performance: () => import('./Page.vue')
  • Give routes descriptive names for easier navigation
  • Use meta fields for authentication and authorization
  • Keep route paths simple and RESTful
  • Document complex routes with comments

❌ DON'T:
  • Hardcode paths everywhere - use named routes instead
  • Duplicate route configurations
  • Put complex logic in route definitions - use beforeEnter guards
  • Forget to handle 404 errors (add a catch-all route)


🔒 AUTHENTICATION EXAMPLE
══════════════════════════════════════════════════════════════════════════════

{
  path: '/admin',
  name: 'AdminDashboard',
  component: AdminPage,
  meta: {
    title: 'Admin Dashboard',
    requiresAuth: true,              // Must be logged in
    allowedRoles: ['admin']          // Must have admin role
  }
}

// In router's beforeEach guard (usually at bottom of this file):
router.beforeEach((to, from, next) => {
  if (to.meta.requiresAuth && !isLoggedIn()) {
    next('/login')
  } else if (to.meta.allowedRoles && !hasRole(to.meta.allowedRoles)) {
    next('/forbidden')
  } else {
    next()
  }
})


📚 LEARN MORE
══════════════════════════════════════════════════════════════════════════════

Official Vue Router Documentation: https://router.vuejs.org/
Navigation Guards: https://router.vuejs.org/guide/advanced/navigation-guards.html
Route Meta Fields: https://router.vuejs.org/guide/advanced/meta.html

*/






// ══════════════════════════════════════════════════════════════════════════════
// STEP 1: IMPORT DEPENDENCIES
// ══════════════════════════════════════════════════════════════════════════════
/*
This section imports everything the router needs to function.

WHAT ARE IMPORTS?
Imports bring in code from other files so we can use it here. In Vue/JavaScript,
we use the ES6 module system with import statements.

WHY IMPORT AT THE TOP?
  • Clarity: See all dependencies at a glance
  • Organization: Easy to find what components are available
  • Performance: Bundler can optimize imports during build
  • Standards: Following JavaScript best practices

IMPORT CATEGORIES IN THIS FILE:

1. EXTERNAL LIBRARIES (from node_modules)
   - Vue Router: createRouter, createWebHistory
   - JWT Decode: For reading authentication tokens
   - API Service: Our custom service for backend communication
   
   Example: import { createRouter } from 'vue-router'
   
2. LAYOUT COMPONENTS (../components/layout/)
   - Main application pages like Home, About, Profile
   - Pages that regular users see and interact with
   - Organized by functionality, not by user role
   
3. LOGIN COMPONENTS (../components/login/)
   - Authentication-related pages
   - Login, logout, and session management
      
5. SURVEY COMPONENTS (../components/umfragen-pages/)
   - Teacher-facing survey configuration pages
   - Question management and translations
   - "Umfragen" = German for "surveys"
   
6. STUDENT SURVEY COMPONENTS (../components/student-survey/)
   - Student-facing survey pages
   - The actual surveys students fill out
   
7. PLOT COMPONENTS (../components/Plots/)
   - Data visualization pages
   - Charts, graphs, and network diagrams
   - Results display for teachers
   
8. LEGACY COMPONENTS (../legacy/)
   - Older components kept for backwards compatibility
   - May be refactored or removed in future versions
   - Still functional but not part of main app flow

IMPORT SYNTAX EXPLAINED:

Default import:
  import HomePage from '../components/layout/HomePage.vue'
  - Imports the default export from HomePage.vue
  - You choose the name (HomePage in this case)
  
Named import:
  import { createRouter, createWebHistory } from 'vue-router'
  - Imports specific named exports
  - Names must match exactly what's exported

Path prefix:
  - '@/' refers to the /src/ directory (configured in vite.config.js)
  - '../' goes up one directory level
  - Always use .vue extension for Vue components

ADDING NEW COMPONENTS:
When you create a new component and want to use it in a route:
1. Create your .vue file in the appropriate directory
2. Import it here in the correct category
3. Use it in a route definition below
4. Make sure the import path is correct

ORGANIZATION TIPS:
  • Keep related imports together
  • Use comments to separate import groups
  • Alphabetize within groups for easier scanning
  • Remove unused imports to keep the file clean

LAZY LOADING (Advanced):
For better performance, you can lazy-load components:
  component: () => import('../components/HomePage.vue')
  
This loads the component only when the route is visited, reducing initial
bundle size. Consider this for rarely-visited or large components.

NOTE: All components imported here must exist in the file system, or the
build will fail with an error.
*/

// External libraries
import { createRouter, createWebHistory } from 'vue-router';
import { jwtDecode } from 'jwt-decode';
import apiService from '@/services/apiService';

// Survey Results / internal dashboards
import UserAnalysis from '../components/survey_results/UserAnalysis.vue';
import DataExport from '../components/survey_results/DataExport.vue';

// SEQ Admin
import AdminQuestionnaireList from '../components/admin/AdminQuestionnaireList.vue';
import AdminQuestionnaireDetail from '../components/admin/AdminQuestionnaireDetail.vue';
import AdminSurveyList from '../components/admin/AdminSurveyList.vue';
import AdminSurveyDetail from '../components/admin/AdminSurveyDetail.vue';
import QuestionEditor from '../components/admin/question_editors/QuestionEditor.vue';
import AdminQuestionsList from '../components/admin/AdminQuestionsList.vue';

// Layout components
import HomePage from '../components/layout/HomePage.vue';
import About1 from '../components/layout/About1.vue';
import MessagesPage from '../components/layout/MessagesPage.vue';
import GroupChatPage from '../components/layout/GroupChatPage.vue';
import FeedbackDisplayPage from '../components/layout/FeedbackDisplayPage.vue';
import ShowLoginsPage from '../components/layout/ShowLoginsPage.vue';
import MyOrganizationsPage from '../components/layout/MyOrganizationsPage.vue';
import UserMgmtPage from '../components/layout/UserMgmtPage.vue';
import ProfilePage from '../components/layout/ProfilePage.vue';

// Login components
import LogoutConfirmationPage from '../components/login/LogoutConfirmationPage.vue';
import LoginPage from '../components/login/LoginPage.vue';
import LogoutPage from '../components/login/LogoutPage.vue';



// Plot components
import DataSelector from '../components/Plots/DataSelector.vue';

// Legacy components
import ConnectToMiroPage from '../legacy/ConnectToMiroPage.vue';
import HelloWorld from '../legacy/HelloWorld.vue';
import CopyMiroBoardPage from '../legacy/CopyMiroBoardPage.vue';


// Image library components
import ImageBrowser from '../components/image_browser/ImageBrowser.vue';
import ImageUploader from '../components/image_uploader/ImageUploader.vue';

// Card editor
import CardEditor from '../components/card_editor/CardEditor.vue';

// Report 1 (dummy report demo)
import Report1 from '../components/layout/Report1.vue';

// Quiz layout variants (design exploration)
import VanillaA from '../components/quiz/vanilla/VanillaA.vue';
import VanillaB from '../components/quiz/vanilla/VanillaB.vue';
import VanillaC from '../components/quiz/vanilla/VanillaC.vue';
import VanillaD from '../components/quiz/vanilla/VanillaD.vue';
import VanillaE from '../components/quiz/vanilla/VanillaE.vue';
import VanillaF from '../components/quiz/vanilla/VanillaF.vue';
import VanillaG from '../components/quiz/vanilla/VanillaG.vue';
import EditorialA from '../components/quiz/editorial/EditorialA.vue';
import EditorialB from '../components/quiz/editorial/EditorialB.vue';
import EditorialF from '../components/quiz/editorial/EditorialF.vue';

// Consent flow (entry point before the quiz)
import AgeChooser from '../components/quiz/consent/AgeChooser.vue';
import AdultConsent from '../components/quiz/consent/AdultConsent.vue';
import ChildParentalConsent from '../components/quiz/consent/ChildParentalConsent.vue';
import ChildTeacherConsent from '../components/quiz/consent/ChildTeacherConsent.vue';
import ConsentReviewHarness from '../components/quiz/consent/ConsentReviewHarness.vue';

// Results variants (post-quiz score visualization)
import Results1 from '../components/quiz/results/Results1.vue';
import Results2 from '../components/quiz/results/Results2.vue';
import Results3 from '../components/quiz/results/Results3.vue';
import Results4 from '../components/quiz/results/Results4.vue';
import Results5 from '../components/quiz/results/Results5.vue';
import Results6 from '../components/quiz/results/Results6.vue';
import Results7 from '../components/quiz/results/Results7.vue';
import Results8 from '../components/quiz/results/Results8.vue';
import Results9 from '../components/quiz/results/Results9.vue';
import Results10 from '../components/quiz/results/Results10.vue';
import Results11 from '../components/quiz/results/Results11.vue';
import Results12 from '../components/quiz/results/Results12.vue';

// Advice pages (post-results, deeper guidance)
import Advice1 from '../components/quiz/advice/Advice1.vue';
import Advice2 from '../components/quiz/advice/Advice2.vue';
import Advice3 from '../components/quiz/advice/Advice3.vue';
import Advice4 from '../components/quiz/advice/Advice4.vue';
import Advice5 from '../components/quiz/advice/Advice5.vue';

// Post-survey flow (after the quiz)
import EmailOptIn from '../components/quiz/postsurvey/EmailOptIn.vue';
import EmailOptInResults from '../components/quiz/postsurvey/EmailOptInResults.vue';
import EmailOptInCode from '../components/quiz/postsurvey/EmailOptInCode.vue';
import ThankYou from '../components/quiz/postsurvey/ThankYou.vue';

// ══════════════════════════════════════════════════════════════════════════════
// STEP 2: DEFINE ROUTES
// ══════════════════════════════════════════════════════════════════════════════
/*
This section defines ALL the routes (pages) available in your application.

WHAT IS A ROUTE?
A route is a mapping between a URL path and a Vue component. When a user visits
a URL, the router finds the matching route and displays the associated component.

Example: User visits "/about" → Router finds the AboutPage route → Renders About1.vue

ROUTE ORGANIZATION IN THIS FILE:
Routes are organized into logical groups to make them easier to find and maintain:
  
  1. Layout routes        - Main pages (Home, About, Profile, etc.)
  2. Login routes         - Authentication pages (Login, Logout)
  4. Survey routes        - Teacher survey management pages
  5. Student survey routes- Student-facing survey pages
  6. Plot routes          - Data visualization and results pages
  7. Legacy routes        - Older pages kept for backwards compatibility

WHY GROUP ROUTES?
  ✓ Easier to find specific routes
  ✓ Easier to maintain and update
  ✓ Clear separation of concerns
  ✓ Better code organization as your app grows

ROUTE ARRAY STRUCTURE:
Each route is an object in the routes array with properties like:
  - path: The URL (e.g., '/about')
  - name: A unique identifier (e.g., 'AboutPage')
  - component: The Vue component to render
  - meta: Additional data (title, authentication requirements, etc.)

See the comprehensive documentation at the top of this file for all available
route properties and examples.

BEST PRACTICES USED HERE:
  • Consistent naming: Component names match route names
  • Clear paths: URLs are descriptive and RESTful
  • Meta titles: Every route sets a browser tab title
  • Access control: Protected routes use meta.requiresAuth and meta.allowedRoles
  • Props: Dynamic routes pass URL parameters as props to components

ADDING A NEW ROUTE:
1. Import the component at the top of this file
2. Add a new route object to the appropriate group below
3. Set the path, name, component, and any meta properties needed
4. Test by navigating to the new route in your browser

NOTE: The order of routes matters! More specific routes should come before
generic ones. For example, '/user/profile' should be defined before '/user/:id'
*/

const routes = [
  // Layout routes
  {
    path: '/',
    name: 'HomePаge',
    component: HomePage,
    meta: {
      title: 'Swiss Eating Quiz - Start'
    }
  },
  {
    path: '/about',
    name: 'AboutPage',
    component: About1,
    meta: {
      title: 'Swiss Eating Quiz - About'
    }
  },
  {
    path: '/messagespage',
    name: 'MessagesPage',
    component: MessagesPage,
    meta: {
      title: 'Swiss Eating Quiz - My Messages',
      requiresAuth: true,
      requiresAdmin: true
    }
  },
  {
    path: '/groupchatpage',
    name: 'GroupChatPage',
    component: GroupChatPage,
    meta: {
      title: 'Swiss Eating Quiz - Chat',
      requiresAuth: true,
      requiresAdmin: true
    }
  },
  {
    path: '/feedback-display',
    name: 'FeedbackDisplayPage',
    component: FeedbackDisplayPage,
    meta: {
      title: 'Swiss Eating Quiz - Feedback',
      requiresAuth: true,
      requiresAdmin: true  // Admin only
    }
  },
  {
    path: '/showloginspage',
    name: 'ShowLoginsPage',
    component: ShowLoginsPage,
    meta: {
      title: 'Swiss Eating Quiz - Logins',
      requiresAuth: true,
      requiresAdmin: true
    }
  },
  {
    path: '/myorganizationspage',
    name: 'MyOrganizationsPage',
    component: MyOrganizationsPage,
    meta: {
      title: 'Swiss Eating Quiz - Organizations',
      requiresAuth: true,
      allowedRoles: ['admin', 'org_admin']  // Either role can access
      // requiresAdmin: true
    }
  },
  {
    path: '/user-mgmt',
    name: 'UserMgmtPage',
    component: UserMgmtPage,
    meta: {
      title: 'Swiss Eating Quiz - Users',
      requiresAuth: true,
      allowedRoles: ['admin', 'org_admin']  // Either role can access
      // requiresAdmin: true
    }
  },
  {
    path: '/profile',
    name: 'ProfilePage',
    component: ProfilePage,
    meta: {
      title: 'Swiss Eating Quiz - My Account',
      requiresAuth: true,
      requiresAdmin: true
    }
  },

  // Login routes
  {
    path: '/login',
    name: 'LoginPage',
    component: LoginPage,
    meta: {
      title: 'Swiss Eating Quiz - Login'
    }
  },
  {
    path: '/logout-confirmation',
    name: 'LogoutConfirmationPage',
    component: LogoutConfirmationPage,
    meta: {
      title: 'Swiss Eating Quiz - Logout'
    }
  },
  {
    path: '/logout',
    name: 'LogoutPage',
    component: LogoutPage,
    meta: {
      title: 'Swiss Eating Quiz - Logout'
    }
  },




  {
    path: '/dataselector',
    name: 'DataSelector',
    component: DataSelector,
    meta: {
      title: 'Swiss Eating Quiz - Data Selection'
    }
  },


  {
    path: '/connect-to-miro',
    name: 'ConnectToMiroPage',
    component: ConnectToMiroPage,
    meta: {
      title: 'Swiss Eating Quiz - Connect to Miro'
    }
  },
  {
    path: '/hello-world',
    name: 'HelloWorld',
    component: HelloWorld,
    meta: {
      title: 'Swiss Eating Quiz - Hello World'
    }
  },
  // Image library
  {
    path: '/images/browse',
    name: 'ImageBrowser',
    component: ImageBrowser,
    meta: {
      title: 'Swiss Eating Quiz - Image Browser'
    }
  },
  {
    path: '/images/upload',
    name: 'ImageUploader',
    component: ImageUploader,
    meta: {
      title: 'Swiss Eating Quiz - Image Uploader'
    }
  },

  // Card editor (moved under /admin/* alongside questionnaires and questions).
  // The old path is preserved as a redirect so existing bookmarks still work.
  {
    path: '/admin/cards/edit',
    name: 'AdminCardEditor',
    component: CardEditor,
    meta: {
      title: 'Swiss Eating Quiz - Cards'
    }
  },
  {
    path: '/cards/edit',
    redirect: '/admin/cards/edit',
  },

  // Report 1 (demo report, accessible to all users)
  {
    path: '/report1',
    name: 'Report1',
    component: Report1,
    meta: {
      title: 'Swiss Eating Quiz - Report 1'
    }
  },

  {
    path: '/copy-miro-board',
    name: 'CopyMiroBoardPage',
    component: CopyMiroBoardPage,
    meta: {
      title: 'Swiss Eating Quiz - Copy Miro Board'
    }
  },

  // Survey Results / internal dashboards
  //
  // /admin/survey-results is the real one: figures computed from collected
  // data, with the invented mock-ups kept below a "To be implemented"
  // heading so the design is visible without being mistaken for delivered
  // work. The two older routes below it are pure mock-ups and stay hidden
  // from the menu.
  {
    path: '/admin/survey-results',
    name: 'AdminSurveyResults',
    component: () => import('../components/admin/AdminSurveyResults.vue'),
    meta: { title: 'Swiss Eating Quiz - Survey Results' }
  },
  {
    path: '/survey-results/user-analysis',
    name: 'UserAnalysis',
    component: UserAnalysis,
    meta: { title: 'Swiss Eating Quiz - User Analysis' }
  },
  {
    path: '/survey-results/data-export',
    name: 'DataExport',
    component: DataExport,
    meta: { title: 'Swiss Eating Quiz - Data Exports' }
  },

  // SEQ Admin
  {
    path: '/admin/questionnaires',
    name: 'AdminQuestionnaireList',
    component: AdminQuestionnaireList,
    meta: { title: 'Swiss Eating Quiz - Questionnaires' }
  },
  {
    path: '/admin/questionnaires/:slug',
    name: 'AdminQuestionnaireDetail',
    component: AdminQuestionnaireDetail,
    meta: { title: 'Swiss Eating Quiz - Questionnaire' }
  },
  {
    path: '/admin/surveys',
    name: 'AdminSurveyList',
    component: AdminSurveyList,
    meta: { title: 'Swiss Eating Quiz - Surveys' }
  },
  {
    path: '/admin/surveys/:slug',
    name: 'AdminSurveyDetail',
    component: AdminSurveyDetail,
    meta: { title: 'Swiss Eating Quiz - Survey' }
  },
  {
    path: '/admin/questions',
    name: 'AdminQuestionsList',
    component: AdminQuestionsList,
    meta: { title: 'Swiss Eating Quiz - Questions' }
  },
  {
    path: '/admin/questions/new',
    name: 'QuestionEditorNew',
    component: QuestionEditor,
    meta: { title: 'Swiss Eating Quiz - New question' }
  },
  {
    path: '/admin/questions/:id/edit',
    name: 'QuestionEditorEdit',
    component: QuestionEditor,
    meta: { title: 'Swiss Eating Quiz - Edit question' }
  },

  // Consent flow (entry point before the quiz)
  {
    // Step 1 review harness for the consolidated consent flow card.
    // Review only: no engine, no persistence. The legacy /consent/* screens
    // below are left untouched for side-by-side comparison.
    path: '/consent',
    name: 'ConsentReviewHarness',
    component: ConsentReviewHarness,
    meta: { title: 'Swiss Eating Quiz - Einverständnis (Review)' }
  },
  {
    path: '/consent/age',
    name: 'AgeChooser',
    component: AgeChooser,
    meta: { title: 'Swiss Eating Quiz - Willkommen' }
  },
  {
    path: '/consent/adult',
    name: 'AdultConsent',
    component: AdultConsent,
    meta: { title: 'Swiss Eating Quiz - Einwilligung (Erwachsene)' }
  },
  {
    path: '/consent/child-parental',
    name: 'ChildParentalConsent',
    component: ChildParentalConsent,
    meta: { title: 'Swiss Eating Quiz - Einwilligung (Eltern)' }
  },
  {
    path: '/consent/child-teacher',
    name: 'ChildTeacherConsent',
    component: ChildTeacherConsent,
    meta: { title: 'Swiss Eating Quiz - Einwilligung (Lehrperson)' }
  },

  // Quiz runtime: the workflow engine that walks an actual questionnaire.
  // The engine runs in two modes, distinguished by route name rather than
  // by a prop, so a link decides which one you get.
  //
  //   /s/:slug         LIVE. :slug is a SURVEY. Renders the frozen revision
  //                    that survey serves, creates a submission at consent,
  //                    and saves every answer as it is entered. This is the
  //                    URL participants are given.
  //
  //   /quiz/run/:slug  PREVIEW. :slug is a QUESTIONNAIRE. Renders the live
  //                    library so an author sees unsaved edits, and writes
  //                    nothing at all. Never hand this to a participant:
  //                    their answers would vanish.
  //
  // See docs/CONTRACT_DATA_CAPTURE.md and docs/CONTRACT_CARD_ENGINE.md.
  {
    path: '/s/:slug',
    name: 'QuizLive',
    component: () => import('../components/quiz/runtime/QuizRuntime.vue'),
    meta: { title: 'Swiss Eating Quiz' }
  },
  {
    path: '/quiz/run/:slug',
    name: 'QuizRuntime',
    component: () => import('../components/quiz/runtime/QuizRuntime.vue'),
    meta: { title: 'Swiss Eating Quiz - Preview' }
  },

  // Static design mockup (standalone HTML, bundled + shown in an iframe).
  {
    path: '/mockups/card-wide-layouts',
    name: 'MockupWideLayouts',
    component: () => import('../components/mockups/WideLayoutsMockup.vue'),
    meta: { title: 'Mockup · Card wide layouts' }
  },

  // Dev-only preview harness for the short_text question card. Lets the
  // card be exercised (render / capture / validity / state-changed) before
  // the backend can author and serve a short_text question. Remove once
  // authoring + persistence land. See
  // docs/SPEC_short_text_and_number_question_cards.md.
  {
    path: '/quiz/preview/short-text',
    name: 'ShortTextCardHarness',
    component: () => import('../components/quiz/runtime/cards/ShortTextCardHarness.vue'),
    meta: { title: 'short_text card preview harness' }
  },

  // Quiz layout variants (design exploration)
  {
    path: '/quiz/vanilla/a',
    name: 'QuizVanillaA',
    component: VanillaA,
    meta: { title: 'Swiss Eating Quiz - Vanilla A · Card stack' }
  },
  {
    path: '/quiz/vanilla/b',
    name: 'QuizVanillaB',
    component: VanillaB,
    meta: { title: 'Swiss Eating Quiz - Vanilla B · Sectioned scroll' }
  },
  {
    path: '/quiz/vanilla/c',
    name: 'QuizVanillaC',
    component: VanillaC,
    meta: { title: 'Swiss Eating Quiz - Vanilla C · Conversational' }
  },
  {
    path: '/quiz/vanilla/d',
    name: 'QuizVanillaD',
    component: VanillaD,
    meta: { title: 'Swiss Eating Quiz - Vanilla D · Hybrid' }
  },
  {
    path: '/quiz/vanilla/e',
    name: 'QuizVanillaE',
    component: VanillaE,
    meta: { title: 'Swiss Eating Quiz - Vanilla E · Categories' }
  },
  {
    path: '/quiz/vanilla/f',
    name: 'QuizVanillaF',
    component: VanillaF,
    meta: { title: 'Swiss Eating Quiz - Vanilla F · Dopamine hit' }
  },
  {
    path: '/quiz/vanilla/g',
    name: 'QuizVanillaG',
    component: VanillaG,
    meta: { title: 'Swiss Eating Quiz - Vanilla G · Resume quiz' }
  },
  {
    path: '/quiz/editorial/a',
    name: 'QuizEditorialA',
    component: EditorialA,
    meta: { title: 'Swiss Eating Quiz - Editorial A · Card stack' }
  },
  {
    path: '/quiz/editorial/b',
    name: 'QuizEditorialB',
    component: EditorialB,
    meta: { title: 'Swiss Eating Quiz - Editorial B · Sectioned scroll' }
  },
  {
    path: '/quiz/editorial/f',
    name: 'QuizEditorialF',
    component: EditorialF,
    meta: { title: 'Swiss Eating Quiz - Editorial F · Dopamine hit' }
  },

  // Results variants
  {
    path: '/results/1',
    name: 'Results1',
    component: Results1,
    meta: { title: 'Swiss Eating Quiz - Ergebnis · Score-first' }
  },
  {
    path: '/results/2',
    name: 'Results2',
    component: Results2,
    meta: { title: 'Swiss Eating Quiz - Ergebnis · Radar' }
  },
  {
    path: '/results/3',
    name: 'Results3',
    component: Results3,
    meta: { title: 'Swiss Eating Quiz - Ergebnis · Conversational' }
  },
  {
    path: '/results/4',
    name: 'Results4',
    component: Results4,
    meta: { title: 'Swiss Eating Quiz - Report · Desktop' }
  },
  {
    path: '/results/5',
    name: 'Results5',
    component: Results5,
    meta: { title: 'Swiss Eating Quiz - Report · Dietician' }
  },
  {
    path: '/results/6',
    name: 'Results6',
    component: Results6,
    meta: { title: 'Swiss Eating Quiz - Report · Health Professional' }
  },
  {
    path: '/results/7',
    name: 'Results7',
    component: Results7,
    meta: { title: 'Swiss Eating Quiz - Report · Nutrition - School Teacher' }
  },
  {
    path: '/results/8',
    name: 'Results8',
    component: Results8,
    meta: { title: 'Swiss Eating Quiz - Report · Nutrition - Students' }
  },
  {
    path: '/results/9',
    name: 'Results9',
    component: Results9,
    meta: { title: 'Swiss Eating Quiz - Report · Sustainability' }
  },
  {
    path: '/results/10',
    name: 'Results10',
    component: Results10,
    meta: { title: 'Swiss Eating Quiz - Report · Sustainability - User' }
  },
  {
    path: '/results/11',
    name: 'Results11',
    component: Results11,
    meta: { title: 'Swiss Eating Quiz - Report · Sustainability - School Classroom' }
  },
  {
    path: '/results/12',
    name: 'Results12',
    component: Results12,
    meta: { title: 'Swiss Eating Quiz - Report · Gen-Z' }
  },

  // Advice pages
  {
    path: '/advice/1',
    name: 'Advice1',
    component: Advice1,
    meta: { title: 'Swiss Eating Quiz - Lebensmittelgruppe' }
  },
  {
    path: '/advice/2',
    name: 'Advice2',
    component: Advice2,
    meta: { title: 'Swiss Eating Quiz - Aktionsplan' }
  },
  {
    path: '/advice/3',
    name: 'Advice3',
    component: Advice3,
    meta: { title: 'Swiss Eating Quiz - Rezepte' }
  },
  {
    path: '/advice/4',
    name: 'Advice4',
    component: Advice4,
    meta: { title: 'Swiss Eating Quiz - Zusammenfassung' }
  },
  {
    path: '/advice/5',
    name: 'Advice5',
    component: Advice5,
    meta: { title: 'Swiss Eating Quiz - Ernährungsbasics' }
  },

  // Post-survey flow
  {
    path: '/postsurvey/email',
    name: 'EmailOptIn',
    component: EmailOptIn,
    meta: { title: 'Swiss Eating Quiz - Erinnerung' }
  },
  {
    path: '/postsurvey/email-results',
    name: 'EmailOptInResults',
    component: EmailOptInResults,
    meta: { title: 'Swiss Eating Quiz - E-Mail mit Ergebnis' }
  },
  {
    path: '/postsurvey/email-code',
    name: 'EmailOptInCode',
    component: EmailOptInCode,
    meta: { title: 'Swiss Eating Quiz - E-Mail mit Code' }
  },
  {
    path: '/postsurvey/thanks',
    name: 'ThankYou',
    component: ThankYou,
    meta: { title: 'Swiss Eating Quiz - Vielen Dank' }
  },

];




// ══════════════════════════════════════════════════════════════════════════════
// STEP 3: CREATE THE ROUTER INSTANCE
// ══════════════════════════════════════════════════════════════════════════════
/*
This section creates the actual router instance that Vue will use.

createRouter() - Creates a new router instance with two key configurations:
  1. history: Defines how URLs are managed
  2. routes: The array of route definitions we created above

HISTORY MODES:
- createWebHistory(): Uses HTML5 History API (clean URLs: /about)
  * Requires server configuration to handle all routes
  * Best for production
  
- createWebHashHistory(): Uses hash in URLs (#/about)
  * Works without server configuration
  * Useful for static hosting

process.env.BASE_URL: The base path for your app (usually '/')
*/

const router = createRouter({
  history: createWebHistory(process.env.BASE_URL),
  routes
});



// ══════════════════════════════════════════════════════════════════════════════
// STEP 4: AUTHENTICATION HELPER FUNCTIONS
// ══════════════════════════════════════════════════════════════════════════════
/*
These utility functions help manage user authentication and authorization.

WHY SEPARATE FUNCTIONS?
- Code Reusability: Use the same logic in multiple places
- Maintainability: Change authentication logic in one place
- Testability: Easier to test isolated functions
- Readability: Makes the beforeEach guard cleaner

isTokenExpired(token):
  - Checks if a JWT token is still valid
  - Decodes the token to read its expiration time
  - Compares expiration time with current time
  - Returns true if expired, false if still valid

checkAuthentication():
  - Retrieves token and user data from apiService
  - Validates the token
  - Returns an object with authentication status and user info
  - Used by navigation guards to make access decisions
*/

// Helper function to check if the token is expired
function isTokenExpired(token) {
  if (!token) return true;

  try {
    const decodedToken = jwtDecode(token);
    const currentTime = Date.now() / 1000;
    return decodedToken.exp < currentTime;
  } catch (error) {
    console.error('Error decoding token:', error);
    return true;  // If there's an error decoding, treat it as expired
  }
}

// Helper function to check authentication using new system
function checkAuthentication() {
  const token = apiService.getAuthToken();
  const user = apiService.getCurrentUser();

  // Check if token exists and is not expired
  const isTokenValid = token && !isTokenExpired(token);

  return {
    token,
    user,
    isTokenValid,
    isAuthenticated: isTokenValid && user,
    userRole: user ? user.role : null
  };
}



// ══════════════════════════════════════════════════════════════════════════════
// STEP 5: GLOBAL NAVIGATION GUARD (beforeEach)
// ══════════════════════════════════════════════════════════════════════════════
/*
This is the SECURITY CHECKPOINT for your entire application.

router.beforeEach() runs BEFORE EVERY route navigation and decides whether to:
  - Allow the navigation (call next())
  - Redirect to another route (call next({ name: 'SomeRoute' }))
  - Cancel the navigation (call next(false))

NAVIGATION FLOW:
  1. User tries to visit a route
  2. beforeEach intercepts the navigation
  3. Checks authentication status using helper functions
  4. Checks route requirements (meta.requiresAuth, meta.allowedRoles, etc.)
  5. Makes decision: allow, redirect, or block

PARAMETERS:
  - to: The route being navigated TO
  - from: The route being navigated FROM
  - next: Callback function to control navigation

AUTHENTICATION CHECKS:
  1. Public routes: Anyone can access (no meta.requiresAuth)
  2. Protected routes: Must be logged in (meta.requiresAuth = true)
  3. Role-based routes: Must have specific role (meta.allowedRoles array)
  4. Legacy support: Handles old requiresAdmin/requiresTeacher flags

ROLE-BASED ACCESS CONTROL:
  - allowedRoles: ['admin', 'teacher'] means either admin OR teacher can access
  - If user's role is not in the array, they're redirected to HomePage
  - This provides flexible, fine-grained access control

STUDENT ACCESS:
  - Legacy PIN-based system for students
  - Checks sessionStorage for student login status
  - Students can access student-specific routes without JWT tokens

PAGE TITLES:
  - Sets document.title based on route meta.title
  - Provides better user experience and SEO
  - Falls back to 'Swiss Eating Quiz' if no title specified

IMPORTANT: You MUST call next() exactly once in every code path, or navigation will hang!
*/

router.beforeEach((to, from, next) => {
  // Check authentication status
  const auth = checkAuthentication();

  // Check if student is logged in (legacy support for student PIN system)
  const isStudentLoggedIn = sessionStorage.getItem('studentLoggedIn') === 'true';

  // Navigation debugging (only for development)
  // console.log('Target route:', to.name);
  // console.log('Token present:', !!auth.token);
  // console.log('Token expired:', auth.token ? isTokenExpired(auth.token) : true);
  // console.log('User role:', auth.userRole);
  // console.log('Is authenticated:', auth.isAuthenticated);
  // console.log('Is student logged in:', isStudentLoggedIn);

  // Special handling for student login page
  if (to.name === 'LoginStudentPage') {
    next();
    return;
  }

  // Check if route requires authentication
  if (to.meta.requiresAuth) {
    if (!auth.isAuthenticated) {
      console.log('User not authenticated, redirecting to LoginPage');
      // Clear invalid token data
      if (auth.token && isTokenExpired(auth.token)) {
        apiService.clearAuthData();
      }
      next({ name: 'LoginPage' });
      return;
    }

// Check role-based access using allowedRoles array
if (to.meta.allowedRoles && to.meta.allowedRoles.length > 0) {
  if (!to.meta.allowedRoles.includes(auth.userRole)) {
    console.log(`Access denied, user role '${auth.userRole}' not in allowed roles:`, to.meta.allowedRoles);
    next({ name: 'HomePage' });
    return;
  }
}

// Keep legacy support for existing routes
if (to.meta.requiresAdmin && to.meta.requiresTeacher) {
  if (auth.userRole !== 'admin' && auth.userRole !== 'teacher') {
    console.log('Access denied, user is not admin or teacher');
    next({ name: 'HomePage' });
    return;
  }
} else if (to.meta.requiresAdmin && auth.userRole !== 'admin') {
  console.log('Access denied, user is not admin');
  next({ name: 'HomePage' });
  return;
} else if (to.meta.requiresTeacher && auth.userRole !== 'teacher') {
  console.log('Access denied, user is not teacher');
  next({ name: 'HomePage' });
  return;
}

    console.log('Access granted to protected route');
    next();
  } else if (isStudentLoggedIn) {
    // Student routes (PIN-based system)
    console.log('Student is logged in, proceeding to route');
    next();
  } else {
    // Public routes
    console.log('Access granted to public route');
    next();
  }

  // Set page title from route meta or use default
  document.title = to.meta.title || 'Swiss Eating Quiz';
});



// ══════════════════════════════════════════════════════════════════════════════
// STEP 5b: SURVIVE A DEPLOY THAT HAPPENS MID-SESSION
// ══════════════════════════════════════════════════════════════════════════════
/*
router.onError() catches errors thrown while a navigation is in flight,
including the case where a lazily-loaded route component fails to download.

Why that happens here:

Most routes above are declared as `component: () => import('...')`. That is
code splitting: webpack puts each of those components in its own file (a
"chunk") named with a content hash, like 448.c5b71fa1.js, and the browser
fetches it only when you first navigate there. The hash changes whenever the
file's contents change, which is what makes the aggressive caching in
frontend/serve.json safe.

The catch is that a browser tab holds the app shell it booted with. Deploy a
new build while somebody has the app open, and their tab is still running the
old shell, which knows only the OLD chunk filenames. The moment they click a
route they have not visited yet, it requests a chunk that no longer exists.

Worse, they do not get a clean 404. `serve -s` answers anything it cannot
find with index.html and a 200, so the browser receives HTML where it expected
JavaScript, fails to parse it, and the navigation rejects. To the user the
click simply does nothing, which is a genuinely baffling thing to debug.

The fix is to reload onto the current build. Reloading re-fetches index.html
(which serve.json marks no-cache), picks up the new shell, and the chunk it
then asks for exists.

The sessionStorage guard matters: if a chunk is missing for some reason a
reload cannot fix, we would otherwise reload forever. One attempt per session,
cleared as soon as any navigation succeeds.

This matters most for participants. Deploying during data collection puts
anyone mid-quiz in exactly this position.
*/
const CHUNK_RELOAD_KEY = 'seq_chunk_reload';

router.onError((error, to) => {
  const message = String((error && error.message) || '');
  const isChunkFailure = /ChunkLoadError/i.test(message)
    || /Loading chunk \S+ failed/i.test(message)
    || /Loading CSS chunk \S+ failed/i.test(message)
    || /error loading dynamically imported module/i.test(message)
    || /Importing a module script failed/i.test(message);

  if (!isChunkFailure) return;

  try {
    if (sessionStorage.getItem(CHUNK_RELOAD_KEY)) return;
    sessionStorage.setItem(CHUNK_RELOAD_KEY, '1');
  } catch (e) {
    // Private browsing can throw on sessionStorage. Without the guard a
    // reload loop is possible, so decline to reload rather than risk it.
    return;
  }

  console.warn('Chunk failed to load (likely a deploy mid-session). Reloading onto the current build.');
  window.location.assign(to.fullPath);
});

// Any successful navigation proves the current shell is intact, so re-arm
// the guard for the next deploy this tab lives through.
router.afterEach(() => {
  try {
    sessionStorage.removeItem(CHUNK_RELOAD_KEY);
  } catch (e) {
    // Nothing to clean up if sessionStorage is unavailable.
  }
});



// ══════════════════════════════════════════════════════════════════════════════
// STEP 6: EXPORT THE ROUTER
// ══════════════════════════════════════════════════════════════════════════════
/*
Export the configured router so it can be imported and used in main.js

In main.js, this router will be added to the Vue app:
  import router from './router'
  app.use(router)

This makes the router available throughout your entire application.
*/
export default router;







